import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../contexts/AuthContext';
import { exportToCsv, exportToExcel, exportToPdf } from '../../helpers/exportCsv';
import { TIPO_ATENDIMENTO_LABEL } from '../../helpers/insumoChecklist';
import logoCentrao from '../../assets/logo-centrao.png';
import {
  PERIODOS, formatarCodigo, fmtDataHora, fmtNum, fmtVariacao, periodo, variacao,
} from '../../helpers/estoque';

// Extrato de movimentacoes. Sem `produto` e o extrato geral com todos os
// filtros; com `produto` e o extrato individual (filtros de produto ocultos).
// A exportacao usa exatamente as linhas filtradas que estao na tela.

const FILTROS_VAZIOS = {
  busca: '', categoria_id: '', subcategoria_id: '', marca_id: '', modelo: '',
  tipo: '', usuario: '', filial: '', setor: '',
};

export default function ExtratoMovimentacoes({ produto = null, categorias = [], marcas = [], tipos = [] }) {
  const [periodoChave, setPeriodoChave] = useState(produto ? 'tudo' : '7dias');
  const [datas, setDatas] = useState(periodo('7dias'));
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [exportando, setExportando] = useState(false);

  const tiposMap = useMemo(() => Object.fromEntries(tipos.map((t) => [t.chave, t])), [tipos]);
  const principais = categorias.filter((c) => !c.parent_id);
  const subs = categorias.filter((c) => c.parent_id && c.parent_id === Number(filtros.categoria_id));
  const set = (k, v) => setFiltros((f) => ({ ...f, [k]: v }));

  const consultar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params = {};
      if (periodoChave !== 'tudo') Object.assign(params, datas);
      if (produto) params.insumo_id = produto.id;
      Object.entries(filtros).forEach(([k, v]) => { if (String(v).trim()) params[k] = String(v).trim(); });
      const res = produto
        ? await axios.get(`${API}/insumos/${produto.id}/historico`)
        : await axios.get(`${API}/insumos/extrato`, { params: { ...params, limite: 5000 } });
      let dados = res.data.data || [];
      // O extrato individual vem completo; aplica periodo/tipo aqui.
      if (produto) {
        dados = dados.filter((m) => {
          const dia = new Date(m.created_at);
          const d = `${dia.getFullYear()}-${String(dia.getMonth() + 1).padStart(2, '0')}-${String(dia.getDate()).padStart(2, '0')}`;
          if (periodoChave !== 'tudo' && (d < datas.de || d > datas.ate)) return false;
          if (filtros.tipo && m.tipo !== filtros.tipo) return false;
          return true;
        });
      }
      setLinhas(dados);
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível carregar o extrato.');
    } finally {
      setCarregando(false);
    }
  }, [periodoChave, datas, filtros, produto]);

  // Consulta automatica ao abrir e ao trocar periodo; os demais filtros
  // aplicam no botao (evita uma consulta a cada tecla).
  useEffect(() => { consultar(); }, [periodoChave, datas]); // eslint-disable-line react-hooks/exhaustive-deps

  const escolherPeriodo = (chave) => {
    setPeriodoChave(chave);
    if (chave !== 'personalizado' && chave !== 'tudo') setDatas(periodo(chave));
  };

  const nomeTipo = (m) => tiposMap[m.tipo]?.label || m.tipo;
  const local = (m) => [m.atendimento_local, m.filial, m.setor, m.destino]
    .filter((v, i, arr) => v && arr.indexOf(v) === i).join(' · ');

  // Motivo legivel. Linhas do checklist: "Atendimento #12 · Manutenção ·
  // resp. Fulano" em vez do texto tecnico gravado.
  const motivo = (m) => {
    if (m.checklist_id) {
      const partes = [`Atendimento #${m.checklist_id}`];
      if (m.atendimento_tipo) partes.push(TIPO_ATENDIMENTO_LABEL[m.atendimento_tipo] || m.atendimento_tipo);
      if (m.atendimento_responsavel) partes.push(`resp. ${m.atendimento_responsavel}`);
      if (m.atendimento_chamado_id) partes.push(`Chamado #${m.atendimento_chamado_id}`);
      if (m.tipo === 'devolucao') partes.push('material não usado voltou');
      return partes.join(' · ');
    }
    return m.motivo || '';
  };
  const referencia = (m) => m.referencia || (m.checklist_id ? `Checklist #${m.checklist_id}` : '');

  // Quanto foi consumido nesta saida (o backend calcula; null = ainda nao se sabe).
  const gasto = (m) => {
    if (m.qtd_gasta !== null && m.qtd_gasta !== undefined) return fmtNum(m.qtd_gasta);
    if (m.aguardando_retorno) return 'aguardando retorno';
    return '';
  };
  const usadoDevolvido = (m) => (m.qtd_usada !== null && m.qtd_usada !== undefined
    ? `usado ${fmtNum(m.qtd_usada)} · devolvido ${fmtNum(m.qtd_devolvida)}` : '');

  const colunas = [
    { label: 'Data', value: (m) => fmtDataHora(m.created_at).data, peso: 1.1 },
    { label: 'Hora', value: (m) => fmtDataHora(m.created_at).hora, peso: 0.7 },
    { label: 'Código', value: (m) => formatarCodigo(m.numero), peso: 0.8 },
    { label: 'Produto', value: (m) => [m.produto, m.marca_nome, m.modelo].filter(Boolean).join(' '), peso: 2.4 },
    { label: 'Categoria', value: (m) => [m.categoria_nome, m.subcategoria_nome].filter(Boolean).join(' › '), peso: 1.6, soPlanilha: true },
    { label: 'Movimento', value: nomeTipo, peso: 1.7 },
    { label: 'Quantidade', value: (m) => fmtVariacao(variacao(m, tiposMap)), peso: 1, alinhar: 'right' },
    { label: 'Saldo anterior', value: (m) => fmtNum(m.estoque_anterior), peso: 1, alinhar: 'right' },
    { label: 'Saldo', value: (m) => fmtNum(m.estoque_resultante), peso: 0.9, alinhar: 'right' },
    { label: 'Gasto', value: gasto, peso: 1.1, alinhar: 'right' },
    { label: 'Usado / devolvido', value: usadoDevolvido, peso: 1.6, soPlanilha: true },
    { label: 'Usuário', value: (m) => m.autor || '', peso: 1.1 },
    { label: 'Local', value: local, peso: 1.5 },
    { label: 'Motivo', value: motivo, peso: 2.6 },
    { label: 'Observação', value: (m) => m.observacao || '', peso: 1.8, soPlanilha: true },
    { label: 'Referência', value: referencia, peso: 1.2, soPlanilha: true },
  ];

  const periodoTexto = periodoChave !== 'tudo'
    ? `${datas.de.split('-').reverse().join('/')} a ${datas.ate.split('-').reverse().join('/')}`
    : 'todo o período';
  const titulo = produto
    ? `Extrato — ${produto.descricao} (código ${formatarCodigo(produto.numero)})`
    : `Extrato de movimentações — ${periodoTexto}`;
  const arquivo = `extrato-estoque-${new Date().toISOString().slice(0, 10)}`;

  // Resumo dos filtros aplicados, impresso no PDF.
  const filtrosTexto = () => {
    const partes = [`Período: ${periodoTexto}`];
    const cat = categorias.find((c) => c.id === Number(filtros.categoria_id));
    const sub = categorias.find((c) => c.id === Number(filtros.subcategoria_id));
    const marca = marcas.find((m) => m.id === Number(filtros.marca_id));
    if (filtros.busca) partes.push(`Busca: ${filtros.busca}`);
    if (cat) partes.push(`Categoria: ${cat.nome}${sub ? ` › ${sub.nome}` : ''}`);
    if (marca) partes.push(`Marca: ${marca.nome}`);
    if (filtros.modelo) partes.push(`Modelo: ${filtros.modelo}`);
    if (filtros.tipo) partes.push(`Movimento: ${tiposMap[filtros.tipo]?.label || filtros.tipo}`);
    if (filtros.usuario) partes.push(`Usuário: ${filtros.usuario}`);
    if (filtros.filial) partes.push(`Filial: ${filtros.filial}`);
    if (filtros.setor) partes.push(`Setor: ${filtros.setor}`);
    return partes.join(' · ');
  };

  const exportar = async (formato) => {
    if (!linhas.length) return;
    setExportando(true);
    try {
      if (formato === 'csv') exportToCsv(arquivo, colunas, linhas);
      if (formato === 'xls') exportToExcel(arquivo, colunas, linhas, titulo);
      if (formato === 'pdf') {
        // No PDF (paisagem) ficam as colunas essenciais; planilhas levam todas.
        const colunasPdf = colunas.filter((c) => !c.soPlanilha && !(produto && ['Código', 'Produto'].includes(c.label)));
        await exportToPdf(arquivo, colunasPdf, linhas, { titulo, subtitulo: filtrosTexto(), logo: logoCentrao });
      }
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="estoque-extrato">
      <div className="filter-card estoque-extrato__filtros">
        <div className="vw-chips" style={{ flexBasis: '100%' }}>
          {(produto ? [['tudo', 'Tudo'], ...PERIODOS] : PERIODOS).map(([k, label]) => (
            <button key={k} type="button" className={`vw-chip ${periodoChave === k ? 'vw-chip--on' : 'vw-chip--off'}`} onClick={() => escolherPeriodo(k)}>
              {label}
            </button>
          ))}
        </div>
        {periodoChave === 'personalizado' ? (
          <>
            <label className="portal-field"><span>De</span>
              <input type="date" value={datas.de} onChange={(e) => setDatas((d) => ({ ...d, de: e.target.value }))} />
            </label>
            <label className="portal-field"><span>Até</span>
              <input type="date" value={datas.ate} onChange={(e) => setDatas((d) => ({ ...d, ate: e.target.value }))} />
            </label>
          </>
        ) : null}
        {!produto ? (
          <>
            <label className="portal-field portal-field--grow"><span>Pesquisa rápida</span>
              <input value={filtros.busca} placeholder="Nome ou código (ex.: 003)" onChange={(e) => set('busca', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') consultar(); }} />
            </label>
            <label className="portal-field"><span>Categoria</span>
              <select value={filtros.categoria_id} onChange={(e) => { set('categoria_id', e.target.value); set('subcategoria_id', ''); }}>
                <option value="">Todas</option>
                {principais.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </label>
            <label className="portal-field"><span>Subcategoria</span>
              <select value={filtros.subcategoria_id} onChange={(e) => set('subcategoria_id', e.target.value)} disabled={!subs.length}>
                <option value="">Todas</option>
                {subs.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </label>
            <label className="portal-field"><span>Marca</span>
              <select value={filtros.marca_id} onChange={(e) => set('marca_id', e.target.value)}>
                <option value="">Todas</option>
                {marcas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </label>
            <label className="portal-field"><span>Modelo</span>
              <input value={filtros.modelo} onChange={(e) => set('modelo', e.target.value)} />
            </label>
          </>
        ) : null}
        <label className="portal-field"><span>Movimento</span>
          <select value={filtros.tipo} onChange={(e) => set('tipo', e.target.value)}>
            <option value="">Todos</option>
            {tipos.map((t) => <option key={t.chave} value={t.chave}>{t.label}</option>)}
          </select>
        </label>
        {!produto ? (
          <>
            <label className="portal-field"><span>Usuário</span>
              <input value={filtros.usuario} onChange={(e) => set('usuario', e.target.value)} />
            </label>
            <label className="portal-field"><span>Filial</span>
              <input value={filtros.filial} onChange={(e) => set('filial', e.target.value)} />
            </label>
            <label className="portal-field"><span>Setor</span>
              <input value={filtros.setor} onChange={(e) => set('setor', e.target.value)} />
            </label>
          </>
        ) : null}
        <div className="portal-form-actions estoque-extrato__botoes">
          <button type="button" className="btn-portal btn-portal--primary" onClick={consultar} disabled={carregando}>
            <i className="bi bi-funnel" /> Filtrar
          </button>
          <button type="button" className="btn-portal btn-portal--outline" onClick={() => setFiltros(FILTROS_VAZIOS)}>
            Limpar
          </button>
        </div>
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card">
        <div className="insumo-ck__secao-topo">
          <span className="cell-muted">{carregando ? 'Carregando...' : `${linhas.length} movimentaç${linhas.length === 1 ? 'ão' : 'ões'}`}</span>
          <div className="portal-form-actions">
            {[['xls', 'Excel', 'bi-file-earmark-excel'], ['csv', 'CSV', 'bi-filetype-csv'], ['pdf', 'PDF', 'bi-file-earmark-pdf']].map(([f, label, icon]) => (
              <button key={f} type="button" className="btn-portal btn-portal--outline" disabled={!linhas.length || exportando} onClick={() => exportar(f)}>
                <i className={`bi ${icon}`} /> {label}
              </button>
            ))}
          </div>
        </div>
        {linhas.length ? (
          <div className="table-shell">
            <table className="portal-table estoque-extrato__tabela">
              <thead>
                <tr>
                  <th>Data</th>{!produto ? <><th>Código</th><th>Produto</th></> : null}
                  <th>Movimento</th><th>Qtd</th><th>Saldo</th><th>Gasto</th><th>Usuário</th><th>Local</th><th>Motivo / observação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((m) => {
                  const v = variacao(m, tiposMap);
                  const { data, hora } = fmtDataHora(m.created_at);
                  return (
                    <tr key={m.id}>
                      <td><span className="cell-strong">{data}</span><div className="cell-muted">{hora}</div></td>
                      {!produto ? (
                        <>
                          <td className="cell-muted">{formatarCodigo(m.numero)}</td>
                          <td>
                            <span className="cell-strong">{m.produto}</span>
                            <div className="insumo-ck__detalhe">
                              {[m.marca_nome, m.modelo].filter(Boolean).join(' ')}
                              {m.categoria_nome ? ` · ${m.categoria_nome}${m.subcategoria_nome ? ` › ${m.subcategoria_nome}` : ''}` : ''}
                            </div>
                          </td>
                        </>
                      ) : null}
                      <td>
                        {nomeTipo(m)}
                        {usadoDevolvido(m) ? <div className="insumo-ck__detalhe">{usadoDevolvido(m)}</div> : null}
                      </td>
                      <td className={v > 0 ? 'insumo-ck__ok' : v < 0 ? 'insumo-ck__alerta' : ''}>{fmtVariacao(v)}</td>
                      <td>
                        <strong>{fmtNum(m.estoque_resultante)}</strong>
                        {m.estoque_anterior !== null && m.estoque_anterior !== undefined ? (
                          <div className="insumo-ck__detalhe">antes {fmtNum(m.estoque_anterior)}</div>
                        ) : null}
                      </td>
                      <td>
                        {m.aguardando_retorno && (m.qtd_gasta === null || m.qtd_gasta === undefined)
                          ? <span className="insumo-ck__pendente">aguardando retorno</span>
                          : gasto(m) ? <strong>{gasto(m)}</strong> : <span className="cell-muted">—</span>}
                      </td>
                      <td className="cell-muted">{m.autor || '—'}</td>
                      <td className="cell-muted">{local(m) || '—'}</td>
                      <td className="cell-muted">
                        {motivo(m) || '—'}
                        {m.observacao ? <div className="insumo-ck__detalhe">{m.observacao}</div> : null}
                        {referencia(m) ? <div className="insumo-ck__detalhe">Ref.: {referencia(m)}</div> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : !carregando ? (
          <div className="empty-state">
            <strong>Nenhuma movimentação encontrada</strong>
            <span>Ajuste o período ou os filtros.</span>
          </div>
        ) : null}
      </section>
    </div>
  );
}
